import { useTranslation } from 'react-i18next';
import { colorByStatus } from '../utils/format';

export type StatusKind =
  | 'active'
  | 'deprecated'
  | 'disabled'
  | 'healthy'
  | 'unhealthy'
  | 'unknown'
  | 'enabled'
  | 'pending';

export interface StatusBadgeProps {
  status: StatusKind;
  label?: string;
  size?: 'sm' | 'md';
  pulse?: boolean;
}

export function StatusBadge({ status, label, size = 'md', pulse = false }: StatusBadgeProps) {
  const { t } = useTranslation();

  const STATUS_LABELS: Record<StatusKind, string> = {
    active: t('gateway.shared.status.active'),
    deprecated: t('gateway.shared.status.deprecated'),
    disabled: t('gateway.shared.status.disabled'),
    healthy: t('gateway.shared.status.healthy'),
    unhealthy: t('gateway.shared.status.unhealthy'),
    unknown: t('gateway.shared.status.unknown'),
    enabled: t('gateway.shared.status.enabled'),
    pending: t('gateway.shared.status.pending'),
  };

  const color = colorByStatus(status);
  const displayLabel = label ?? STATUS_LABELS[status] ?? status;
  const fontSize = size === 'sm' ? 'var(--font-size-xs)' : 'var(--font-size-sm)';
  const padding = size === 'sm' ? '1px var(--spacing-1)' : '2px var(--spacing-2)';

  return (
    <span
      role="status"
      aria-live="polite"
      aria-label={displayLabel}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-1)',
        padding,
        fontSize,
        borderRadius: 'var(--radius-full)',
        color,
        border: `1px solid ${color}`,
        background: 'var(--color-surface-background)',
        fontWeight: 500,
        animation: pulse ? 'gateway-pulse 2s infinite' : undefined,
      }}
    >
      <span
        style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          background: color,
          flexShrink: 0,
        }}
      />
      {displayLabel}
    </span>
  );
}
